import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, handleApiError } from '../client';
import type {
  User,
  LoginCredentials,
  RegisterData,
  AuthResponse,
  MagicLinkRequest,
  MagicLinkVerifyRequest,
  OtpRequest,
  OtpVerifyRequest,
  WebAuthnRegistrationOptions,
  WebAuthnAuthenticationOptions,
  WebAuthnCredential,
} from '../types/auth';
import type { ApiError } from '../types/common';

// Query keys
const authKeys = {
  all: ['auth'] as const,
  currentUser: () => [...authKeys.all, 'current-user'] as const,
  webauthnCredentials: () => [...authKeys.all, 'webauthn-credentials'] as const,
};

// Token storage utilities
const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

function storeTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

// ============================================
// Password Authentication
// ============================================

async function login(credentials: LoginCredentials): Promise<AuthResponse> {
  const response = await apiClient.post<AuthResponse>('/auth/login', credentials);
  storeTokens(response.data.accessToken, response.data.refreshToken);
  return response.data;
}

async function register(data: RegisterData): Promise<AuthResponse> {
  const response = await apiClient.post<AuthResponse>('/auth/register', data);
  storeTokens(response.data.accessToken, response.data.refreshToken);
  return response.data;
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation<AuthResponse, ApiError, LoginCredentials>({
    mutationFn: login,
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.currentUser(), data.user);
    },
    onError: (error) => {
      console.error('Login failed:', error);
    },
  });
}

export function useRegister() {
  const queryClient = useQueryClient();

  return useMutation<AuthResponse, ApiError, RegisterData>({
    mutationFn: register,
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.currentUser(), data.user);
    },
    onError: (error) => {
      console.error('Registration failed:', error);
    },
  });
}

// ============================================
// Magic Link Authentication
// ============================================

async function requestMagicLink(data: MagicLinkRequest): Promise<{ message: string; expiresInMinutes: number }> {
  const response = await apiClient.post<{ message: string; expiresInMinutes: number }>(
    '/auth/magic-link/request',
    data
  );
  return response.data;
}

async function verifyMagicLink(data: MagicLinkVerifyRequest): Promise<AuthResponse> {
  const response = await apiClient.post<AuthResponse>('/auth/magic-link/verify', data);
  storeTokens(response.data.accessToken, response.data.refreshToken);
  return response.data;
}

export function useMagicLinkRequest() {
  return useMutation<{ message: string; expiresInMinutes: number }, ApiError, MagicLinkRequest>({
    mutationFn: requestMagicLink,
    onError: (error) => {
      console.error('Magic link request failed:', error);
    },
  });
}

export function useMagicLinkVerify() {
  const queryClient = useQueryClient();

  return useMutation<AuthResponse, ApiError, MagicLinkVerifyRequest>({
    mutationFn: verifyMagicLink,
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.currentUser(), data.user);
    },
    onError: (error) => {
      console.error('Magic link verification failed:', error);
    },
  });
}

export function useMagicLink() {
  return {
    request: useMagicLinkRequest(),
    verify: useMagicLinkVerify(),
  };
}

// ============================================
// OTP Authentication
// ============================================

async function requestOtp(data: OtpRequest): Promise<{ message: string; expiresInSeconds: number }> {
  const response = await apiClient.post<{ message: string; expiresInSeconds: number }>(
    '/auth/otp/request',
    data
  );
  return response.data;
}

async function verifyOtp(data: OtpVerifyRequest): Promise<AuthResponse> {
  const response = await apiClient.post<AuthResponse>('/auth/otp/verify', data);
  storeTokens(response.data.accessToken, response.data.refreshToken);
  return response.data;
}

export function useOtpRequest() {
  return useMutation<{ message: string; expiresInSeconds: number }, ApiError, OtpRequest>({
    mutationFn: requestOtp,
    onError: (error) => {
      console.error('OTP request failed:', error);
    },
  });
}

export function useOtpVerify() {
  const queryClient = useQueryClient();

  return useMutation<AuthResponse, ApiError, OtpVerifyRequest>({
    mutationFn: verifyOtp,
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.currentUser(), data.user);
    },
    onError: (error) => {
      console.error('OTP verification failed:', error);
    },
  });
}

export function useOtp() {
  return {
    request: useOtpRequest(),
    verify: useOtpVerify(),
  };
}

// ============================================
// WebAuthn Authentication
// ============================================

async function getWebAuthnRegistrationOptions(
  email?: string,
  userType?: string
): Promise<WebAuthnRegistrationOptions> {
  const response = await apiClient.post<WebAuthnRegistrationOptions>(
    '/auth/webauthn/register/options',
    { email, userType }
  );
  return response.data;
}

async function verifyWebAuthnRegistration(data: {
  challengeKey: string;
  credential: PublicKeyCredential;
  deviceName?: string;
}): Promise<{ success: boolean; credentialId: string; message: string }> {
  const attestationResponse = data.credential.response as AuthenticatorAttestationResponse;

  const response = await apiClient.post<{ success: boolean; credentialId: string; message: string }>(
    '/auth/webauthn/register/verify',
    {
      challengeKey: data.challengeKey,
      deviceName: data.deviceName,
      credential: {
        id: data.credential.id,
        rawId: arrayBufferToBase64Url(data.credential.rawId),
        type: data.credential.type,
        clientDataJSON: arrayBufferToBase64Url(attestationResponse.clientDataJSON),
        attestationObject: arrayBufferToBase64Url(attestationResponse.attestationObject),
        transports: attestationResponse.getTransports?.() || [],
      },
    }
  );
  return response.data;
}

async function getWebAuthnAuthenticationOptions(
  email?: string,
  userType?: string
): Promise<WebAuthnAuthenticationOptions> {
  const response = await apiClient.post<WebAuthnAuthenticationOptions>(
    '/auth/webauthn/login/options',
    { email, userType }
  );
  return response.data;
}

async function verifyWebAuthnAuthentication(data: {
  challengeKey: string;
  credential: PublicKeyCredential;
}): Promise<AuthResponse> {
  const assertionResponse = data.credential.response as AuthenticatorAssertionResponse;

  const response = await apiClient.post<AuthResponse>('/auth/webauthn/login/verify', {
    challengeKey: data.challengeKey,
    credential: {
      id: data.credential.id,
      rawId: arrayBufferToBase64Url(data.credential.rawId),
      type: data.credential.type,
      clientDataJSON: arrayBufferToBase64Url(assertionResponse.clientDataJSON),
      authenticatorData: arrayBufferToBase64Url(assertionResponse.authenticatorData),
      signature: arrayBufferToBase64Url(assertionResponse.signature),
      userHandle: assertionResponse.userHandle
        ? arrayBufferToBase64Url(assertionResponse.userHandle)
        : undefined,
    },
  });

  storeTokens(response.data.accessToken, response.data.refreshToken);
  return response.data;
}

// WebAuthn credential creation
async function startWebAuthnRegistration(
  email?: string,
  userType?: string,
  deviceName?: string
): Promise<{ success: boolean; credentialId: string; message: string }> {
  // Get registration options from server
  const options = await getWebAuthnRegistrationOptions(email, userType);

  // Convert base64url to ArrayBuffer for WebAuthn API
  const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
    challenge: base64UrlToArrayBuffer(options.challenge),
    rp: options.rp,
    user: {
      id: base64UrlToArrayBuffer(options.user.id),
      name: options.user.name,
      displayName: options.user.displayName,
    },
    pubKeyCredParams: options.pubKeyCredParams.map((p) => ({
      type: p.type as PublicKeyCredentialType,
      alg: p.alg,
    })),
    timeout: options.timeout,
    attestation: (options.attestation || 'none') as AttestationConveyancePreference,
    authenticatorSelection: options.authenticatorSelection,
    excludeCredentials: options.excludeCredentials?.map((c) => ({
      type: c.type as PublicKeyCredentialType,
      id: base64UrlToArrayBuffer(c.id),
    })),
  };

  // Create credential using WebAuthn API
  const credential = (await navigator.credentials.create({
    publicKey: publicKeyCredentialCreationOptions,
  })) as PublicKeyCredential;

  if (!credential) {
    throw new Error('Failed to create credential');
  }

  // Send to server for verification
  return verifyWebAuthnRegistration({
    challengeKey: options.challengeKey,
    credential,
    deviceName,
  });
}

async function startWebAuthnAuthentication(email?: string, userType?: string): Promise<AuthResponse> {
  // Get authentication options from server
  const options = await getWebAuthnAuthenticationOptions(email, userType);

  // Convert base64url to ArrayBuffer for WebAuthn API
  const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
    challenge: base64UrlToArrayBuffer(options.challenge),
    timeout: options.timeout,
    rpId: options.rpId,
    userVerification: (options.userVerification || 'required') as UserVerificationRequirement,
    allowCredentials: options.allowCredentials?.map((c) => ({
      type: c.type as PublicKeyCredentialType,
      id: base64UrlToArrayBuffer(c.id),
    })),
  };

  // Get credential using WebAuthn API
  const credential = (await navigator.credentials.get({
    publicKey: publicKeyCredentialRequestOptions,
  })) as PublicKeyCredential;

  if (!credential) {
    throw new Error('Failed to get credential');
  }

  // Send to server for verification
  return verifyWebAuthnAuthentication({
    challengeKey: options.challengeKey,
    credential,
  });
}

export function useWebAuthnRegister() {
  return useMutation<
    { success: boolean; credentialId: string; message: string },
    ApiError,
    { email?: string; userType?: string; deviceName?: string }
  >({
    mutationFn: ({ email, userType, deviceName }) => startWebAuthnRegistration(email, userType, deviceName),
    onError: (error) => {
      console.error('WebAuthn registration failed:', error);
    },
  });
}

export function useWebAuthnLogin() {
  const queryClient = useQueryClient();

  return useMutation<AuthResponse, ApiError, { email?: string; userType?: string }>({
    mutationFn: ({ email, userType }) => startWebAuthnAuthentication(email, userType),
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.currentUser(), data.user);
    },
    onError: (error) => {
      console.error('WebAuthn authentication failed:', error);
    },
  });
}

export function useWebAuthn() {
  return {
    register: useWebAuthnRegister(),
    login: useWebAuthnLogin(),
    isSupported: isWebAuthnSupported(),
  };
}

// Check if WebAuthn is supported
export function isWebAuthnSupported(): boolean {
  return (
    window.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential === 'function'
  );
}

// ============================================
// Logout and Token Refresh
// ============================================

async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout');
  } finally {
    clearTokens();
  }
}

async function refreshToken(): Promise<AuthResponse> {
  const currentRefreshToken = getRefreshToken();
  if (!currentRefreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await apiClient.post<AuthResponse>('/auth/refresh', {
    refreshToken: currentRefreshToken,
  });

  storeTokens(response.data.accessToken, response.data.refreshToken);
  return response.data;
}

async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<User>('/auth/me');
  return response.data;
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, void>({
    mutationFn: logout,
    onSuccess: () => {
      queryClient.clear();
    },
    onError: (error) => {
      console.error('Logout failed:', error);
      // Still clear local state on error
      clearTokens();
      queryClient.clear();
    },
  });
}

export function useRefreshToken() {
  return useMutation<AuthResponse, ApiError, void>({
    mutationFn: refreshToken,
    onError: (error) => {
      console.error('Token refresh failed:', error);
      // Clear tokens on refresh failure
      clearTokens();
    },
  });
}

export function useCurrentUser() {
  return useQuery({
    queryKey: authKeys.currentUser(),
    queryFn: getCurrentUser,
    enabled: !!getAccessToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401 errors
      if ((error as ApiError).code === '401') {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// Helper hook to check authentication status
export function useAuth() {
  const { data: user, isLoading, error, refetch } = useCurrentUser();

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    error: error ? handleApiError(error) : null,
    refetch,
    hasToken: !!getAccessToken(),
  };
}

// ============================================
// Utility Functions
// ============================================

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function base64UrlToArrayBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const paddedBase64 = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(paddedBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// Export utilities
export { getAccessToken, getRefreshToken, clearTokens, storeTokens };
